import { useState } from "react";
import Modal from "components/modal";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import errorCopy from "utils/error-copy";
import { getUsageData } from "api/usage";
import useModal from "utils/hooks/use-modal";
import Panel from "components/ui/panel";
import { useSearchParams } from "react-router-dom";
import { useStripe } from "@stripe/react-stripe-js";
import ShimmerLoader from "components/shimmer-loader";
import PlanDetails from "sections/modal/plan-details";
import { getSubscriptionData } from "api/subscriptions";
import getFormattedDate from "utils/methods/getFormattedDate";
import capitalizeString from "utils/methods/capitalizeString";
import { getAllStripeProducts, createCheckoutSession } from "api/stripe";

const Overview = ({ choosePlanLabel = "Choose Plan" }) => {
  const [searchParams] = useSearchParams();
  const planValue = searchParams.get("plan");

  const { openModal, closeModal, isOpen } = useModal(planValue ? true : false);

  // stripe hooks and states
  const stripe = useStripe();
  const [loading, setLoading] = useState(null);

  const { data: userUsageData } = useQuery("userUsage", getUsageData, {
    enabled: false,
  });

  const { data: stripeProducts, isLoading: stripeProductsLoading } = useQuery(
    "stripe-products",
    getAllStripeProducts,
    {
      retry: false,
      keepPreviousData: true,
    }
  );

  const {
    data: subscriptionData,
    isLoading: subscriptionLoading,
  } = useQuery("userSubscription", getSubscriptionData, {
    retry: false,
    keepPreviousData: true,
  });

  // The API now returns 200 + null when the user has no active subscription
  // (instead of a 404). `subscriptionData === null` therefore means "loaded,
  // and no subscription" — distinct from undefined (still loading).
  const hasNoSubscription = subscriptionData === null;

  const handleSubmitWithCheckout = async (event, priceId) => {
    event.preventDefault();
    setLoading(priceId);

    try {
      // Call your backend to create the checkout session
      const response = await createCheckoutSession({ priceId });

      // Redirect the user to Stripe Checkout
      const result = await stripe.redirectToCheckout({
        sessionId: response.id,
      });

      if (result.error) {
        toast.error(errorCopy(result.error.message || "Failed creating checkout session"),
          {
            position: "top-right",
          }
        );
      }
    } catch (error) {
      toast.error(errorCopy(error?.response?.data?.error || "That payment didn't go through."), {
        position: "top-right",
      });
    } finally {
      setLoading(null);
    }
  };

  const isToBeRenewed = (date) => {
    const currentDate = new Date(Date.now());
    return new Date(date) > currentDate;
  };

  const ifAllDataLoaded = () => {
    return (
      !subscriptionLoading &&
      !stripeProductsLoading &&
      stripeProducts &&
      subscriptionData
    );
  };

  const isSubscriptionActive = () => {
    return (
      !subscriptionLoading &&
      subscriptionData &&
      (subscriptionData.status === "active" ||
        subscriptionData.status === "trialing")
    );
  };

  const isPeriodMonth = () => {
    const allPrices = [];
    Object.keys(stripeProducts).forEach((el) => {
      allPrices.push(...stripeProducts[el]);
    });
    const priceInfo = allPrices.find(
      (el) => el.id === subscriptionData.planDuration.stripePriceId
    );
    return priceInfo.recurring.interval === "month";
  };

  const displayPrice = () => {
    const allPrices = [];
    Object.keys(stripeProducts).forEach((el) => {
      allPrices.push(...stripeProducts[el]);
    });
    const priceInfo = allPrices.find(
      (el) => el.id === subscriptionData.planDuration.stripePriceId
    );
    return (
      priceInfo.currency.toUpperCase() +
      " " +
      priceInfo.unit_amount / 100 +
      "/" +
      priceInfo.recurring.interval
    );
  };

  const getRenewsDisplayText = () => {
    if (subscriptionData.canceledAt) {
      return "Plan ends";
    }

    if (
      subscriptionData.status === "trialing" &&
      isToBeRenewed(subscriptionData.endDate)
    ) {
      return "Trial ends";
    }

    if (
      subscriptionData.status !== "canceled" &&
      isToBeRenewed(subscriptionData.endDate)
    ) {
      return "Next billing date";
    }

    return "Expired on";
  };

  return (
    <>
      <div className="w-full xl:max-w-[850px]">
        <p className="mb-3 font-body text-ui-sm font-medium text-muted">Current plan</p>

        {!ifAllDataLoaded() && !hasNoSubscription && (
          <div className="flex flex-col lg:flex-row gap-5 xl:h-[144px] w-full justify-between mb-[50px]">
            <ShimmerLoader
              className={
                "h-[144px] xl:h-full w-full mb-[50px] rounded-ns overflow-hidden"
              }
            />
            <ShimmerLoader
              className={
                "h-[144px] xl:h-full w-full mb-[50px] rounded-ns overflow-hidden"
              }
            />
          </div>
        )}

        {ifAllDataLoaded() && (
          <div className="mb-12 flex w-full flex-col justify-between gap-5 xl:flex-row">
            {/* The one green panel on the page. This was a flat shade-50 card
                like everything else, which is why the screen read as a wall of
                grey — the single most important fact on it, what the clinician
                is paying for, had no more weight than the rest. `deep` is one of
                the brand's two sanctioned green treatments (§5) and Panel brings
                the mandatory grain with it. */}
            <Panel ground="deep" className="w-full px-5 py-4 xl:w-[65%]">
              <div className="mb-6 flex w-full items-center justify-between gap-3">
                {(subscriptionLoading || stripeProductsLoading) && (
                  <ShimmerLoader className="h-7 w-[100px]" />
                )}
                {ifAllDataLoaded() && (
                  <span className="font-body text-ui-sm text-white/70">
                    {subscriptionData.planDuration.plan.name} · {isPeriodMonth() ? "Monthly" : "Annual"}
                  </span>
                )}

                {subscriptionLoading && (
                  <ShimmerLoader className="h-[36px] rounded-ns w-28" />
                )}

                {!subscriptionLoading && subscriptionData && (
                  <div
                    className={`flex items-center gap-1.5 rounded-ns-tag px-2.5 py-1 font-body text-ui-xs font-medium ${
                      isSubscriptionActive()
                        ? "bg-accent-light/20 text-accent-light"
                        : "bg-warn/20 text-warn"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`h-1.5 w-1.5 rounded-full ${
                        isSubscriptionActive() ? "bg-accent-light" : "bg-warn"
                      }`}
                    />
                    {isSubscriptionActive()
                      ? "Active"
                      : capitalizeString(subscriptionData.status)}
                  </div>
                )}
              </div>

              {!ifAllDataLoaded() && <ShimmerLoader className="h-6 w-28" />}

              {ifAllDataLoaded() && (
                <span className="font-display text-h4 font-medium text-white">
                  {displayPrice()}
                </span>
              )}
            </Panel>

            {/* The invisible "Placeholder" pill that used to sit here was
                spacing this card's header to match the panel beside it. The two
                cards are the same height by layout now, so it is gone. */}
            <div className="flex w-full flex-col rounded-ns border border-solid border-rule bg-white px-5 py-4 shadow-card xl:w-[35%]">
              <div className="mb-6 flex items-center justify-between">
                {subscriptionLoading && (
                  <ShimmerLoader className="h-7 w-[100px]" />
                )}
                {!subscriptionLoading && subscriptionData && (
                  <span className="font-body text-ui-sm text-muted">
                    {getRenewsDisplayText()}
                  </span>
                )}
              </div>
              {subscriptionLoading && (
                <ShimmerLoader className="h-7 w-[200px]" />
              )}
              {!subscriptionLoading && subscriptionData && (
                <p className="mb-1.5 font-display text-ui-lg font-medium text-ink">
                  {getFormattedDate(subscriptionData.endDate)}
                </p>
              )}
              {!subscriptionLoading && subscriptionData?.status === "trialing" && (
                <p className="font-body text-ui-xs text-muted">
                  Your {subscriptionData.planDuration.plan.name} billing begins after trial
                </p>
              )}
            </div>
          </div>
        )}

        {!ifAllDataLoaded() &&
          hasNoSubscription &&
          userUsageData &&
          userUsageData.isFreeTrial && (
            <div className="mb-8 flex h-auto w-full">
              {/* Same green panel as the paid plan above — this is what a free
                  user sees as their "current plan", and it was the one flat grey
                  card on a page whose whole problem was flat grey cards. */}
              <Panel ground="deep" className="w-full px-5 py-4 xl:w-1/2">
                <div className="mb-6 flex w-full items-center justify-between gap-3">
                  <span className="font-body text-ui-sm text-white/70">Basic</span>
                  <div className="flex items-center gap-1.5 rounded-ns-tag bg-accent-light/20 px-2.5 py-1 font-body text-ui-xs font-medium text-accent-light">
                    <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent-light" />
                    Active
                  </div>
                </div>

                <p className="mb-1.5 font-display text-h4 font-medium text-white">Free Forever</p>
                <p className="font-body text-ui-sm text-white/70">60 sessions / month</p>
              </Panel>
            </div>
          )}

        <div className="mb-12">
          <button
            onClick={() => openModal()}
            /* The one accent action on the page. It was a grey button wearing
               `shadow-pop` — the modal elevation — so it floated without ever
               reading as the primary thing to do here. */
            className="rounded-ns bg-accent px-5 py-2.5 font-body text-ui-sm font-semibold text-white transition-colors duration-ui ease-ui hover:bg-deep active:scale-[.98]"
          >
            {choosePlanLabel}
          </button>
        </div>
      </div>

      {isOpen && (
        <Modal isOpen={isOpen} closeModal={closeModal}>
          <PlanDetails
            loading={loading}
            products={stripeProducts}
            userUsageData={userUsageData}
            subscriptionData={subscriptionData}
            handleClickGetStarted={handleSubmitWithCheckout}
          />
        </Modal>
      )}
    </>
  );
};

export default Overview;